// Mini-app bootstrap: turn a Bale launch into a logged-in Hamyan session.
//
// Runs once, before the router renders, so nobody sees a login screen flash
// before the token lands. It is written to never throw and never block for
// long: every failure path falls through to the ordinary SMS login, which
// still works fine inside the mini-app webview.

import posthog from 'posthog-js'

import { miniappAuth, miniappContactLogin, miniappLink } from 'services/miniapp'
import { ensureSessionProfile, hasSession } from 'utilities/session'
import {
    describeLaunch,
    getInitData,
    getLinkTicket,
    getPlatformHint,
    getRememberedPlatform,
    rememberLinkTicket,
    isAutoLoginSuppressed,
    isMiniapp,
    markMiniappDocument,
    rememberPlatform,
} from './detect'
import { announceReady, deliversContactToBot } from './sdk'
import { flushTelemetry, queueEvent, queueRegister } from './telemetry'
import { markFreshLogin } from './homeScreen'
import type { MiniappPlatform } from './sdk'

export {
    describeLaunch,
    isMiniapp,
    getInitData,
    getLinkTicket,
    getPlatformHint,
    getStartParam,
    suppressAutoLogin,
    markMiniappDocument,
} from './detect'
export { loadSdk, announceReady, deliversContactToBot } from './sdk'
export { flushTelemetry } from './telemetry'
export {
    shouldOfferHomeScreen,
    addToHomeScreen,
    rememberDismissed,
} from './homeScreen'
export {
    requestPhoneFromMessenger,
    canRequestContact,
    toLocalMobile,
} from './contact'
export type { ContactResult } from './contact'

/**
 * Trade a signed shared contact for a session — the zero-SMS path.
 *
 * Returns true when the user is now logged in. Only Eitaa and Telegram can
 * reach here; Bale's unsigned number never gets this far.
 */
export const loginWithSharedContact = async (
    signedResponse: string
): Promise<boolean> => {
    const initData = getInitData()
    if (!initData) return false
    try {
        const result = await miniappContactLogin(initData, signedResponse)
        if (!result?.token) return false
        storeSession(result.token)
        markFreshLogin()
        posthog.capture('miniapp_contact_login', {
            platform: getRememberedPlatform(),
        })
        return true
    } catch {
        // Unsigned, mismatched or refused — the SMS login is still right there.
        posthog.capture('miniapp_contact_login_failed')
        return false
    }
}
export type { MiniappPlatform } from './sdk'

const storeSession = (token: string) => {
    window.localStorage.setItem('user', JSON.stringify({ token }))
}

/**
 * Complete the session and report where we should land, if not here.
 *
 * The profile guarantee itself lives in `utilities/session` because it is not
 * a mini-app concern — every entry point needs it. Only the routing choice
 * below is specific to launching inside a messenger.
 *
 * Returns a path for the caller to navigate to rather than rewriting history
 * directly: the router captures its location when `routes.tsx` is imported,
 * which happens before any of this runs, so a `replaceState` here would be
 * silently ignored.
 */
const completeSession = async (): Promise<string | null> => {
    const redirect = await ensureSessionProfile()
    if (redirect) return redirect

    // A stale token can bounce us onto /auth/login via the 401 interceptor.
    // Now that we hold a working session, don't leave the user staring at a
    // login form they don't need.
    if (window.location.pathname.startsWith('/auth')) return '/'

    return null
}

/**
 * Auto-login for a mini-app launch. Safe to call on every page load.
 *
 * Resolves once we either have a session or know we cannot get one silently;
 * the caller renders the app either way. Resolves to a path the caller should
 * navigate to after rendering, or null to stay put.
 */
export const bootstrapMiniapp = async (): Promise<string | null> => {
    const initData = getInitData()
    if (!initData) return null

    // Before anything renders: lets CSS drop the bottom inset the webview
    // reports but does not need.
    markMiniappDocument()

    // Registered (not just captured) so EVERY later event carries which
    // messenger, client and SDK version it came from — otherwise a
    // platform-specific failure is invisible in aggregate.
    const launch = describeLaunch()
    queueRegister(launch)
    queueEvent('miniapp_launch', launch)

    // A repeat launch already knows the platform, so the client's spinner can
    // be dismissed immediately instead of waiting on the auth round-trip.
    const known = getRememberedPlatform() || getPlatformHint()
    if (known) {
        queueRegister({ miniapp_platform: known })
        announceReady(known)
    }

    // They signed out on purpose in this tab; signing them back in would make
    // the button look broken.
    if (isAutoLoginSuppressed()) return null

    // Already signed in. Still fall through to the profile check below: an
    // earlier build stored the token without ever writing `profile`, and those
    // sessions look healthy right up until the sidebar logs them out.
    if (hasSession()) {
        return completeSession()
    }

    try {
        const result = await miniappAuth(initData)

        // The backend identified the messenger from the payload signature;
        // trust that over anything we could sniff client-side.
        const platform = (result.platform || known) as MiniappPlatform | null
        if (platform && platform !== known) {
            rememberPlatform(platform)
            queueRegister({ miniapp_platform: platform })
            announceReady(platform)
        }

        if (result.linked && result.token) {
            storeSession(result.token)
            // First launch of a real session: the moment the add-to-home-screen
            // nudge is allowed to appear.
            markFreshLogin()
            queueEvent('miniapp_auto_login', { platform })
            return completeSession()
        }

        // Keep the launch proof for after the SMS login. On Eitaa this is the
        // only way the account can ever be bound, since its verification
        // cannot be repeated for the same launch.
        if (result.link_ticket) rememberLinkTicket(result.link_ticket)

        queueEvent('miniapp_login_required', {
            platform,
            reason: result.requires_2fa ? '2fa' : 'not_linked',
        })
    } catch {
        // Forged/expired payload, or the backend is unreachable. The SMS login
        // is a complete fallback, so there is nothing to report to the user.
        queueEvent('miniapp_auto_login_failed', { platform: known })
    }

    return null
}

/**
 * Wait for the bot's webhook to link this account, then sign in.
 *
 * Bale delivers a mini-app `requestContact()` to the BOT — the contact lands in
 * the chat and reaches our webhook server-to-server, which links the account.
 * (Its docs say the number goes only to the page; observed behaviour says
 * otherwise, and the webhook log proves it.) That is the trustworthy channel,
 * but it completes *behind* the page, so nothing tells the mini app it happened.
 *
 * So after asking for the contact we poll our own auth endpoint for a few
 * seconds. The moment the link exists we get a token back and the user is in —
 * no SMS. If it never appears we simply stop, and the SMS login is untouched.
 */
export const waitForContactLink = async (
    attempts = 8,
    delayMs = 1500
): Promise<boolean> => {
    const initData = getInitData()
    if (!initData) return false

    // Skip entirely where nothing can arrive. Eitaa has no bot webhook, so
    // polling there is a guaranteed-useless wait — which is exactly what made
    // its button look broken: twelve silent seconds and no outcome.
    const platform = getRememberedPlatform()
    if (attempts > 1 && platform && !deliversContactToBot(platform)) return false

    for (let attempt = 0; attempt < attempts; attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, delayMs))
        try {
            const result = await miniappAuth(initData)
            if (result.linked && result.token) {
                storeSession(result.token)
                markFreshLogin()
                posthog.capture('miniapp_contact_webhook_login', {
                    platform: getRememberedPlatform(),
                })
                return true
            }
        } catch {
            // Transient; keep waiting for the webhook rather than giving up.
        }
    }
    return false
}

/**
 * One immediate check for "has the link appeared yet?".
 *
 * For the person who leaves the mini app, shares their number in the bot chat,
 * and comes back: the webhook linked them while the page sat idle, so the
 * login screen should not still be asking.
 */
export const checkLinkedNow = (): Promise<boolean> =>
    waitForContactLink(1, 0)

/**
 * Bind this Bale account to the member who just authenticated by SMS.
 *
 * Called after a successful login so the *next* open is instant. Failure is
 * silent on purpose: the user is already logged in, and a toast about an
 * invisible optimisation would only confuse them.
 */
export const linkMiniappAccount = async (): Promise<void> => {
    if (!isMiniapp()) return
    const linkTicket = getLinkTicket()
    const initData = getInitData()
    if (!linkTicket && !initData) return

    try {
        await miniappLink({ linkTicket, initData })
        // Cold start just completed — same moment, via the SMS path.
        markFreshLogin()
        posthog.capture('miniapp_account_linked', {
            platform: getRememberedPlatform(),
        })
    } catch {
        /* next login will try again */
    }
}
